import { type FC, useState } from "react";
import { Button, Menu, MenuItem, Tooltip } from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import type { MarketingLanguageType } from "@/types/graphql/partner.generated";
import { useCurrentUserQuery } from "@/partner/entities/auth";
import {
  marketingErrorKey,
  useSendTestEmailTemplateMutation,
} from "@/partner/entities/marketing";
import { LANGUAGE_LABEL_KEYS } from "../model/blocks";

interface TestSendButtonProps {
  /** id сохранённого письма; null у ещё не созданного. */
  templateId: string | null;
  /** Языки вариантов письма в порядке вкладок. */
  languages: MarketingLanguageType[];
  /** Есть несохранённые правки: тест ушёл бы по старой версии письма. */
  disabled: boolean;
}

/**
 * «Отправить тест себе»: письмо уходит на email текущего сотрудника с
 * примерными данными в персональных подборках. Отправляется сохранённая
 * версия, поэтому у нового письма и при несохранённых правках кнопка
 * неактивна с подсказкой. При нескольких языках сначала меню языков.
 */
export const TestSendButton: FC<TestSendButtonProps> = ({
  templateId,
  languages,
  disabled,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const currentUser = useCurrentUserQuery();
  const email = currentUser.data?.currentUser?.email ?? "";
  const [sendTest, { loading }] = useSendTestEmailTemplateMutation({
    onCompleted: () =>
      enqueueSnackbar(t("emailTemplates.test.sent", { email }), {
        variant: "success",
      }),
    onError: (error) =>
      enqueueSnackbar(t(marketingErrorKey(error)), { variant: "error" }),
  });

  const blocked = !templateId || disabled;
  const send = (language: MarketingLanguageType) => {
    setAnchor(null);
    if (!templateId) return;
    void sendTest({ variables: { id: templateId, language } });
  };

  return (
    <>
      <Tooltip title={blocked ? t("emailTemplates.test.saveFirst") : ""}>
        <span>
          <Button
            startIcon={<SendIcon />}
            disabled={blocked || loading || languages.length === 0}
            onClick={(event) =>
              languages.length > 1
                ? setAnchor(event.currentTarget)
                : send(languages[0])
            }
          >
            {t("emailTemplates.test.send")}
          </Button>
        </span>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
      >
        {languages.map((language) => (
          <MenuItem key={language} onClick={() => send(language)}>
            {t(LANGUAGE_LABEL_KEYS[language])}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};
